import type { PlaybookAddOnType, PlaybookGradient } from "@libertify/ui";
import type { ProjectType } from "pages/media/projects/types";
import type {
  AddonObject,
  AddonSettings,
} from "./context/PlaybookDraftContext";

/**
 * The kind of project the playbook flow produces. The backend doesn't expose a
 * type on playbooks yet, so every playbook-centric experience (including
 * build-your-own, which has no playbook to read it from) is created as
 * "flash_education". TODO: derive per playbook once the backend exposes it.
 */
export const PLAYBOOK_PROJECT_TYPE: ProjectType = "flash_education";

/**
 * Stable view-model the /create-experience UI renders, decoupled from the
 * backend playbook shape. Keep every field the UI depends on here and map into
 * it from a single place (`usePlaybooksForCarousel`) so schema changes stay
 * contained.
 */
export interface CarouselPlaybook {
  id: string;
  name: string;
  description?: string;
  /** Placeholder artwork until real playbook thumbnails exist. */
  gradient: PlaybookGradient;
  /**
   * Whether the playbook already carries a brand kit (on its video add-on
   * settings). Drives `generate_brandkit` at project creation so we don't
   * regenerate a brand kit the playbook already defines.
   */
  hasBrandKit: boolean;
  /**
   * The kind of project this playbook produces. Always
   * `PLAYBOOK_PROJECT_TYPE` until the backend exposes a type per playbook.
   */
  projectType: ProjectType;
  /** Add-ons the playbook bundles, surfaced in the details modal. */
  addOns: PlaybookAddOnType[];
  /**
   * The playbook's add-ons + settings pre-mapped into the draft shape, ready
   * to seed `PlaybookDraftContext` when this playbook is selected.
   */
  addonDraft: AddonObject;
  /** The playbook's `general_*_settings`, seeded into the general slot
   *  alongside `addonDraft` so customizing doesn't drop them. */
  generalDraft: AddonSettings;
  /** Whether the playbook is public. */
  public?: boolean;
  organizationId: string;
}
